'use client'

import { cn } from '@/lib/utils'
import { AnimatedNumber } from '@/components/ui/animated-number'

import { EASE, SectionHead, useReveal } from './shared'

/* The census. Eleven boxes, deliberately unconnected — no rail runs between
   them, which is the entire point — against one unbroken bar underneath.
   The claim "85% reduction in data silos" is asserted as a number in the
   shared template; here it is drawn. No icons appear in this section at all. */

const LEGACY = [
  { name: 'Attendance register', cost: 'Re-keyed daily' },
  { name: 'Fee counter receipt book', cost: 'Re-keyed daily' },
  { name: 'Salary sheet (Excel)', cost: 'Re-keyed monthly' },
  { name: 'Tally / accounting', cost: 'Re-keyed monthly' },
  { name: 'Biometric device logs', cost: 'Exported weekly' },
  { name: 'Exam marks spreadsheet', cost: 'Re-keyed per exam' },
  { name: 'Library issue ledger', cost: 'Re-keyed daily' },
  { name: 'Hostel & mess register', cost: 'Re-keyed daily' },
  { name: 'Transport route diary', cost: 'Re-keyed daily' },
  { name: 'Faculty WhatsApp groups', cost: 'Never recorded' },
  { name: 'NAAC / AICTE compliance file', cost: 'Rebuilt per cycle' },
]

export function UdcSilos() {
  const grid = useReveal<HTMLDivElement>(0.12)

  return (
    <section className="pt-16 sm:pt-20 md:pt-28">
      <div className="container mx-auto">
        <SectionHead
          eyebrow="The problem"
          title="A Tuesday, on eleven systems."
          deck="Most campuses do not lack software. They have too much of it, and none of it talking to the rest. Every box below is a place the same student record gets typed in again."
        />

        <div ref={grid.ref} className="mt-10 sm:mt-12">
          <ul className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-4">
            {LEGACY.map((s, i) => (
              <li
                key={s.name}
                className={cn(
                  'rounded-xl border border-border/70 px-3.5 py-3',
                  'bg-background'
                )}
                style={{
                  opacity: grid.shown ? 1 : 0,
                  transform: grid.shown ? 'none' : 'translateY(8px)',
                  transition: `opacity 420ms ${EASE} ${i * 45}ms, transform 420ms ${EASE} ${i * 45}ms`,
                }}
              >
                <div className="flex items-start gap-2">
                  <span
                    aria-hidden
                    className="mt-[7px] size-1 shrink-0 rounded-full bg-foreground/25"
                  />
                  <div className="min-w-0">
                    <div className="text-[12px] font-medium leading-snug text-foreground/90 sm:text-[13px]">
                      {s.name}
                    </div>
                    <div className="mt-1 text-[10px] tabular-nums text-muted-foreground/70 sm:text-[11px]">
                      {s.cost}
                    </div>
                  </div>
                </div>
              </li>
            ))}

            {/* The twelfth slot is left as a ghost: the systems nobody has
                counted yet. It completes the grid without pretending the
                list is exhaustive. */}
            <li
              aria-hidden
              className="rounded-xl border border-dashed border-border/70 px-3.5 py-3"
              style={{
                opacity: grid.shown ? 1 : 0,
                transition: `opacity 420ms ${EASE} ${LEGACY.length * 45}ms`,
              }}
            >
              <div className="flex h-full items-center">
                <div className="text-[12px] leading-snug text-muted-foreground/60 sm:text-[13px]">
                  …and the ones nobody has counted
                </div>
              </div>
            </li>
          </ul>

          {/* One unbroken bar. Same width as everything above it, none of the
              gaps. */}
          <div
            className={cn(
              'mt-8 flex flex-col gap-4 rounded-2xl border border-emerald-600/30 dark:border-primary-active/25',
              'bg-emerald-600/[0.05] dark:bg-primary-active/[0.05]',
              'px-5 py-5 sm:flex-row sm:items-center sm:justify-between sm:px-7 sm:py-6'
            )}
            style={{
              opacity: grid.shown ? 1 : 0,
              transform: grid.shown ? 'none' : 'translateY(12px)',
              transition: `opacity 600ms ${EASE} 620ms, transform 600ms ${EASE} 620ms`,
            }}
          >
            <div>
              <div className="font-heading text-lg font-normal leading-tight text-foreground sm:text-xl">
                Unified Digital Campus
              </div>
              <div className="mt-1.5 text-[13px] leading-relaxed text-muted-foreground sm:text-sm">
                Entered once. Present everywhere it is needed, the moment it is
                needed.
              </div>
            </div>

            <div className="flex items-baseline gap-3 sm:shrink-0 sm:flex-col sm:items-end sm:gap-0">
              <div className="font-heading text-3xl font-normal tabular-nums leading-none text-emerald-600 sm:text-4xl dark:text-primary-active">
                <AnimatedNumber value="85%" />
              </div>
              <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground sm:mt-2">
                fewer places to keep in sync
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
